import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { askConfirmName } from "../../lib/confirmName";
import { MicButton, useSpeaker } from "./StripeAiVoice";
import { StripeAiJournal } from "./StripeAiJournal";

const SUGGESTIONS = ["Combien de doublons Stripe et combien ça me coûte ?", "Qui est en retard de paiement ou en défaut ?", "Où en est l'objectif du mois par discipline ?",
  "Quels membres viennent au dojo sans payer ?", "Quelles sont mes 3 priorités financières aujourd'hui ?", "Qui devrais-je archiver ou relancer ?"];

const money = (n) => `${Number(n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;

function Md({ text }) {
  return (
    <div className="space-y-1">
      {(text || "").split("\n").filter((l) => l.trim()).map((l, i) => {
        const parts = l.replace(/^\s*[-•*]\s+/, "• ").split(/(\*\*[^*]+\*\*)/g);
        return <p key={i} className="leading-snug">{parts.map((p, j) => p.startsWith("**") ? <b key={j} className="text-zinc-50">{p.slice(2, -2)}</b> : p)}</p>;
      })}
    </div>
  );
}

// Exécution d'une action proposée par l'IA — toujours après confirmation de Ralph
async function runAction(a) {
  const p = a.params || {};
  switch (a.type) {
    case "cancel_subscription": {
      const confirm_name = askConfirmName("Annuler un abonnement Stripe (Stripe IA)", p.name || p.subscription_id);
      if (!confirm_name) throw new Error("Annulé — nom non confirmé");
      return api.post("/ops/audit/cancel-subscription", { subscription_id: p.subscription_id, reason: "doublon (Stripe IA)", confirm_name });
    }
    case "refund_last_charge": {
      const lc = (await api.get(`/ops/audit/last-charge/${p.subscription_id}`)).data;
      if (!lc.charge_id) throw new Error("Aucun prélèvement payé trouvé");
      if (lc.refunded) throw new Error(`Déjà remboursé (${money(lc.refunded)})`);
      return api.post("/ops/audit/refund-last-charge", { subscription_id: p.subscription_id, charge_id: lc.charge_id });
    }
    case "queue_card_update_sms": return api.post("/ops/audit/queue-card-update-sms");
    case "queue_comeback": return api.post("/ops/audit/queue-comeback");
    case "create_missing_contracts": return api.post("/ops/audit/create-missing-contracts");
    case "delete_test_customers": return api.post("/ops/audit/delete-test-customers");
    case "queue_complete_profile": return api.post("/ops/audit/queue-complete-profile");
    case "archive": return api.post("/ops/audit/archive", { contract_ids: p.contract_ids || [] });
    case "open_stripe": window.open(p.url, "_blank", "noreferrer"); return null;
    default: throw new Error("Action inconnue");
  }
}

function ActionChip({ a, onDone, ctx }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const res = await runAction(a);
      setDone(true); setConfirm(false);
      if (a.type !== "open_stripe") {
        toast.success(`${a.label} — fait`);
        api.post("/ops/stripe-ai/actions", { type: a.type, label: a.label, params: a.params || {}, result: res?.data || {}, session_id: ctx?.sid, question: ctx?.question || "" }).catch(() => {});
        onDone();
      }
    }
    catch (e) { toast.error(e?.response?.data?.detail || e.message || "Erreur"); }
    finally { setBusy(false); }
  };
  const key = a.params?.subscription_id || (a.params?.contract_ids || []).join("-") || a.type;
  if (done) return <span className="px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[11px]" data-testid={`sai-action-done-${key}`}>✓ {a.label}</span>;
  return (
    <span className="inline-flex items-center gap-1" data-testid={`sai-action-${key}`}>
      {!confirm ? (
        <button onClick={() => (a.type === "open_stripe" ? go() : setConfirm(true))} title={a.why} className="px-2.5 py-1 rounded-full border border-[#C9A961]/60 text-[#C9A961] hover:bg-[#C9A961]/10 text-[11px] font-semibold transition-colors" data-testid={`sai-action-btn-${key}`}>
          {a.type === "open_stripe" ? "↗ " : "⚡ "}{a.label}
        </button>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/50 bg-rose-500/10 px-2 py-0.5 text-[11px]">
          <span className="text-rose-200">Confirmer « {a.label} »{a.params?.name ? ` — ${a.params.name}` : ""} ?</span>
          <button onClick={go} disabled={busy} className="px-2 py-0.5 rounded-full bg-rose-600 hover:bg-rose-500 text-white font-semibold disabled:opacity-50" data-testid={`sai-action-confirm-${key}`}>{busy ? "…" : "Oui"}</button>
          <button onClick={() => setConfirm(false)} className="px-1.5 text-zinc-400 hover:text-zinc-200" data-testid={`sai-action-cancel-${key}`}>Non</button>
        </span>
      )}
    </span>
  );
}

// Santé Stripe › Audit : assistant conversationnel « Stripe IA » (données Stripe live + KAIZEN) + analyse quotidienne 6 h
export function StripeAiChat({ onActionDone: onActionDoneProp }) {
  const [sid, setSid] = useState(() => localStorage.getItem("stripe_ai_sid") || "");
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const [autoSpeak, setAutoSpeak] = useState(() => localStorage.getItem("stripe_ai_speak") === "1");
  const [journalKey, setJournalKey] = useState(0);
  const speaker = useSpeaker();
  const endRef = useRef(null);
  const loadBrief = () => api.get("/ops/stripe-ai/brief").then((r) => setBrief(r.data)).catch(() => {});
  useEffect(() => { loadBrief(); if (sid) api.get(`/ops/stripe-ai/history?session_id=${sid}`).then((r) => setMsgs(r.data.messages || [])).catch(() => {}); }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  const send = async (text, { speak = false } = {}) => {
    const m = (text ?? q).trim();
    if (!m || busy) return;
    setQ(""); setBusy(true);
    setMsgs((x) => [...x, { role: "user", content: m, at: new Date().toISOString() }]);
    try {
      const r = (await api.post("/ops/stripe-ai/chat", { message: m, session_id: sid || undefined })).data;
      if (!sid) { setSid(r.session_id); localStorage.setItem("stripe_ai_sid", r.session_id); }
      setMsgs((x) => [...x, { role: "assistant", content: r.answer, actions: r.actions, at: r.at }]);
      if (speak || autoSpeak) speaker.speak(r.answer);
    } catch (e) { toast.error(e?.response?.data?.detail || "Stripe IA indisponible"); setMsgs((x) => x.slice(0, -1)); }
    finally { setBusy(false); }
  };
  const onActionDone = () => { setJournalKey((k) => k + 1); onActionDoneProp?.(); };
  const lastQuestion = [...msgs].reverse().find((m) => m.role === "user")?.content || "";
  const newChat = () => { setSid(""); setMsgs([]); localStorage.removeItem("stripe_ai_sid"); };
  const runBrief = async () => {
    setBriefBusy(true);
    try { await api.post("/ops/stripe-ai/brief/run"); toast.success("Analyse IA régénérée"); loadBrief(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur IA"); }
    finally { setBriefBusy(false); }
  };
  const b = brief?.brief;
  const sevCls = { ok: "border-emerald-500/40 text-emerald-300", attention: "border-amber-500/50 text-amber-300", urgent: "border-rose-500/60 text-rose-300" }[b?.severity] || "border-zinc-700 text-zinc-300";
  return (
    <div className="rounded-xl border border-[#C9A961]/30 bg-zinc-900/70 mt-4 overflow-hidden" data-testid="stripe-ai-panel">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="stripe-ai-toggle">
        <div>
          <h3 className="font-semibold text-sm text-zinc-100">✦ Stripe IA — pose tes questions sur tes revenus</h3>
          <p className="text-[11px] text-zinc-500">Données Stripe LIVE + KAIZEN, en français · propose des actions que TU confirmes · analyse automatique chaque matin 6 h (SMS 7 h + courriel)</p>
        </div>
        <span className="text-xs text-zinc-400">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className={`rounded-lg border ${sevCls.split(" ")[0]} bg-black/30 p-3 text-xs`} data-testid="stripe-ai-brief">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className={`font-semibold ${sevCls.split(" ")[1]}`}>☀️ Analyse du matin {b ? `· ${b.date} · ${b.severity.toUpperCase()}` : ""}{brief && b && !brief.is_today ? " (pas encore générée aujourd'hui)" : ""}</p>
              <button onClick={runBrief} disabled={briefBusy} className="px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[11px] disabled:opacity-50" data-testid="stripe-ai-brief-run">{briefBusy ? "Analyse en cours… ~30 s" : b ? "Régénérer maintenant" : "Générer l'analyse"}</button>
            </div>
            {b ? <div className="mt-2 text-zinc-300"><Md text={b.brief} />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {b.audio_url && <a href={b.audio_url} target="_blank" rel="noreferrer" className="px-2.5 py-1 rounded-full border border-[#C9A961]/60 text-[#C9A961] hover:bg-[#C9A961]/10 text-[11px] font-semibold" data-testid="stripe-ai-brief-audio">🔊 Écouter l'analyse (audio)</a>}
                <p className="text-zinc-500">SMS 7 h → « IA ({b.severity}): {b.sms} »{b.audio_url ? " + lien 🔊 Écouter" : ""}</p>
              </div></div>
              : <p className="mt-1 text-zinc-500">Aucune analyse encore. Elle sera générée automatiquement à 6 h et résumée dans ton SMS de 7 h.</p>}
          </div>
          <div className="rounded-lg border border-zinc-800 bg-black/40 max-h-[26rem] overflow-y-auto p-3 space-y-3" data-testid="stripe-ai-messages">
            {msgs.length === 0 && (
              <div className="text-xs text-zinc-500">
                <p className="mb-2">Exemples de questions :</p>
                <div className="flex flex-wrap gap-1.5">{SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="px-2.5 py-1 rounded-full border border-zinc-700 hover:border-[#C9A961]/60 hover:text-zinc-100 transition-colors" data-testid="stripe-ai-suggestion">{s}</button>)}</div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`stripe-ai-msg-${m.role}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs ${m.role === "user" ? "bg-[#C9A961] text-black rounded-br-sm" : "bg-zinc-800/90 text-zinc-200 rounded-bl-sm border border-zinc-700/60"}`}>
                  {m.role === "user" ? m.content : <Md text={m.content} />}
                  {m.role === "assistant" && <button type="button" onClick={() => (speaker.speaking ? speaker.stop() : speaker.speak(m.content))} className="mt-1 text-[10px] text-zinc-400 hover:text-zinc-100" data-testid="stripe-ai-speak-msg">{speaker.speaking ? "⏹ arrêter" : speaker.loading ? "…" : "🔊 écouter"}</button>}
                  {m.actions?.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5 pt-2 border-t border-zinc-700/60">{m.actions.map((a, j) => <ActionChip key={j} a={a} onDone={onActionDone} ctx={{ sid, question: lastQuestion }} />)}</div>}
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-start" data-testid="stripe-ai-thinking"><div className="rounded-2xl rounded-bl-sm bg-zinc-800/90 border border-zinc-700/60 px-3 py-2 text-xs text-zinc-400 animate-pulse">Stripe IA lit tes données Stripe + KAIZEN… 20-60 s</div></div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex flex-wrap gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ex. : qui a deux abonnements pour le même produit ?" className="flex-1 min-w-[12rem] bg-black border border-zinc-700 focus:border-[#C9A961]/70 rounded-lg px-3 py-2 text-xs text-zinc-100 outline-none" data-testid="stripe-ai-input" />
            <MicButton disabled={busy} onPress={speaker.unlock} onTranscript={(t) => send(t, { speak: true })} />
            <button type="submit" disabled={busy || !q.trim()} className="px-4 py-2 rounded-lg bg-[#C9A961] hover:bg-[#d8b970] text-black text-xs font-semibold disabled:opacity-40" data-testid="stripe-ai-send">Demander</button>
            <button type="button" onClick={() => { const v = !autoSpeak; setAutoSpeak(v); localStorage.setItem("stripe_ai_speak", v ? "1" : "0"); if (v) speaker.unlock(); }} className={`px-3 py-2 rounded-lg text-xs ${autoSpeak ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300"}`} title="Lire chaque réponse à voix haute" data-testid="stripe-ai-autospeak">{autoSpeak ? "🔊 Voix ON" : "🔇 Voix"}</button>
            {msgs.length > 0 && <button type="button" onClick={newChat} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs" data-testid="stripe-ai-new">Nouvelle conversation</button>}
          </form>
          <p className="text-[10px] text-zinc-600">🎙 Appuie, pose ta question, appuie de nouveau pour envoyer — la réponse est lue à voix haute (voix OpenAI, accent légèrement anglophone).</p>
          <StripeAiJournal refreshKey={journalKey} />
        </div>
      )}
    </div>
  );
}
